import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { z } from "zod";

const emailSchema = z.string().email("Please enter a valid email address");

interface WaitlistFormProps {
  source?: string;
  className?: string;
  variant?: "default" | "hero" | "inline";
}

export const WaitlistForm = ({ source = "landing_page", className = "", variant = "default" }: WaitlistFormProps) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    const validation = emailSchema.safeParse(email);
    if (!validation.success) {
      const issues = validation.error.issues;
      if (issues && issues.length > 0) {
        setError(issues[0].message);
      } else {
        setError("Please enter a valid email address");
      }
      return;
    }

    setIsLoading(true);
    
    try {
      // Use edge function with server-side rate limiting
      const { data, error: fnError } = await supabase.functions.invoke("waitlist-signup", {
        body: { email: email.toLowerCase().trim(), source },
      });

      if (fnError) {
        // Check for rate limit error
        if (fnError.message?.includes("Too many attempts")) {
          setError(fnError.message);
          return;
        }
        throw fnError;
      }

      if (data?.error) {
        if (data.retryAfter) {
          // Rate limited
          setError(data.error);
          return;
        }
        throw new Error(data.error);
      }

      setIsSuccess(true);
      toast({
        title: data.message || "You're on the list!",
        description: "We'll notify you when Serento is ready.",
      });
    } catch (err) {
      console.error("Waitlist error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className={`flex items-center gap-3 p-4 rounded-xl bg-success/10 border border-success/20 ${className}`}>
        <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0" />
        <span className="text-success font-medium">You're on the list! We'll be in touch soon.</span>
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <form onSubmit={handleSubmit} className={`flex flex-col sm:flex-row gap-3 ${className}`}>
        <div className="flex-1">
          <Input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-12 text-base bg-background border-border"
            disabled={isLoading}
          />
          {error && <p className="text-destructive text-sm mt-1">{error}</p>}
        </div>
        <Button type="submit" size="lg" className="h-12 px-6 whitespace-nowrap" disabled={isLoading}>
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              Join Serento (Early Access)
              <ArrowRight className="ml-2 w-4 h-4" />
            </>
          )}
        </Button>
      </form>
    );
  }

  if (variant === "hero") {
    return (
      <form onSubmit={handleSubmit} className={`w-full max-w-md mx-auto ${className}`}>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-14 text-base bg-card border-border shadow-sm"
              disabled={isLoading}
            />
          </div>
          <Button type="submit" size="lg" className="h-14 px-8 shadow-primary" disabled={isLoading}>
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                Join Serento
                <ArrowRight className="ml-2 w-4 h-4" />
              </>
            )}
          </Button>
        </div>
        {error && <p className="text-destructive text-sm mt-2 text-center">{error}</p>}
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`space-y-4 ${className}`}>
      <div>
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-12 text-base"
          disabled={isLoading}
        />
        {error && <p className="text-destructive text-sm mt-1">{error}</p>}
      </div>
      <Button type="submit" className="w-full h-12" disabled={isLoading}>
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>
            Join Serento (Early Access)
            <ArrowRight className="ml-2 w-4 h-4" />
          </>
        )}
      </Button>
    </form>
  );
};
